import { useEffect, useMemo, useRef, useState } from "react";
import { previewTiers, type PreviewQuality } from "@veyso/core";
import { api, type ProjectDetail } from "../api.js";

export interface PreviewProxy {
  /** Storage key the player should use, or undefined to fall back. */
  key: string | undefined;
  /** True while a proxy for the chosen tier is being transcoded. */
  preparing: boolean;
  /** Actual pixel width being played, for the debug overlay. */
  width: number | null;
  error: string | null;
}

const POLL_MS = 3000;

/**
 * Resolves the proxy for a quality tier, asking the worker to build it if the
 * project has not got one yet.
 *
 * Tiers are built on demand rather than all at once at ingest: most projects
 * are only ever previewed at one quality, and transcoding three copies of a
 * 4K recording to find that out is a poor trade. While a tier is being made the
 * editor keeps playing whichever proxy it already has, so switching quality
 * never leaves the preview blank.
 */
export function usePreviewProxy(
  projectId: string,
  project: ProjectDetail | null,
  quality: PreviewQuality,
  enabled: boolean,
): PreviewProxy {
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const requested = useRef(new Set<number>());

  const wanted = previewTiers[quality].width;

  const proxies = useMemo(
    () => (project?.assets ?? []).filter((asset) => asset.kind === "proxy" && asset.width),
    [project],
  );

  const exact = proxies.find((asset) => asset.width === wanted);
  // Anything already built beats a blank frame while the right one transcodes.
  const nearest = useMemo(
    () => [...proxies].sort((a, b) => Math.abs((a.width ?? 0) - wanted) - Math.abs((b.width ?? 0) - wanted))[0] ?? null,
    [proxies, wanted],
  );

  useEffect(() => {
    if (!enabled || !project || exact) {
      setPreparing(false);
      return;
    }
    if (requested.current.has(wanted)) return;
    requested.current.add(wanted);

    let cancelled = false;
    setPreparing(true);
    setError(null);

    void (async () => {
      try {
        const response = await api.requestProxy(projectId, wanted);
        if (response.status === "ready") {
          if (!cancelled) setNonce((value) => value + 1);
          return;
        }
        // Poll the project rather than the job: the asset appearing is the
        // thing the player actually needs.
        for (let attempt = 0; attempt < 200 && !cancelled; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, POLL_MS));
          const detail = await api.getProject(projectId).catch(() => null);
          if (detail?.assets.some((asset) => asset.kind === "proxy" && asset.width === wanted)) {
            if (!cancelled) setNonce((value) => value + 1);
            return;
          }
        }
      } catch (cause) {
        if (!cancelled) setError((cause as Error).message);
      } finally {
        if (!cancelled) setPreparing(false);
        requested.current.delete(wanted);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, project, exact, wanted, enabled, nonce]);

  if (!enabled) return { key: undefined, preparing: false, width: null, error: null };

  const chosen = exact ?? nearest;
  return {
    key: chosen?.key,
    preparing: preparing && !exact,
    width: chosen?.width ?? null,
    error,
  };
}
